import {
  Room,
  PUBLIC_ROOM,
  type ClientMessage,
  type LeagueRow,
  type MeetResultRow,
  type Programme,
  type RecentMeet,
  type RoomHost,
  type ServerMessage,
} from '@decathlon/shared';
import { programmeName } from '@decathlon/shared';

export type Status = 'connecting' | 'open' | 'closed';

export interface Transport {
  readonly kind: 'online' | 'local';
  connect(): void;
  close(): void;
  send(msg: ClientMessage): void;
  onMessage(cb: (msg: ServerMessage) => void): void;
  onStatus(cb: (s: Status) => void): void;
}

export class WsTransport implements Transport {
  readonly kind = 'online';
  private ws: WebSocket | null = null;
  private msgCb: ((m: ServerMessage) => void) | null = null;
  private statusCb: ((s: Status) => void) | null = null;
  private closed = false;
  private attempts = 0;
  private join: Extract<ClientMessage, { type: 'join' }> | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private resume: string | undefined;
  private storageKey: string;
  constructor(code = PUBLIC_ROOM) {
    this.storageKey = `decathlon.resume.${code}`;
    try {
      this.resume = sessionStorage.getItem(this.storageKey) ?? undefined;
    } catch {
      /* unavailable storage */
    }
  }

  connect(): void {
    this.closed = false;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.statusCb?.('connecting');
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    ws.addEventListener('open', () => {
      if (this.closed || this.ws !== ws) return;
      this.attempts = 0;
      this.statusCb?.('open');
      if (this.join)
        ws.send(JSON.stringify({ ...this.join, ...(this.resume ? { resume: this.resume } : {}) }));
    });
    ws.addEventListener('message', (e) => {
      if (this.closed || this.ws !== ws) return;
      try {
        const msg = JSON.parse(String(e.data)) as ServerMessage;
        if (msg.type === 'welcome' && msg.resume) {
          this.resume = msg.resume;
          if (msg.room && this.join) {
            this.join = { ...this.join, createRoom: false, room: msg.room.code };
            this.storageKey = `decathlon.resume.${msg.room.code}`;
          }
          try {
            sessionStorage.setItem(this.storageKey, msg.resume);
          } catch {
            /* unavailable */
          }
        }
        this.msgCb?.(msg);
      } catch {
        /* ignore */
      }
    });
    ws.addEventListener('close', () => {
      if (this.closed || this.ws !== ws) return;
      this.statusCb?.('closed');
      const delay = Math.min(10_000, 500 * 2 ** this.attempts++);
      this.retry = setTimeout(() => {
        this.retry = null;
        if (!this.closed) this.connect();
      }, delay);
    });
    ws.addEventListener('error', () => ws.close());
  }

  close(): void {
    this.closed = true;
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'leave' }));
    this.ws?.close();
    this.resume = undefined;
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch {
      /* unavailable */
    }
  }

  send(msg: ClientMessage): void {
    if (msg.type === 'join') this.join = msg;
    const data = JSON.stringify(
      msg.type === 'join' && this.resume ? { ...msg, resume: this.resume } : msg,
    );
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data);
  }

  onMessage(cb: (m: ServerMessage) => void): void {
    this.msgCb = cb;
  }
  onStatus(cb: (s: Status) => void): void {
    this.statusCb = cb;
  }
}

/** In-browser room for practice. The league persists in localStorage. */
export class LocalTransport implements Transport {
  readonly kind = 'local';
  private room: Room;
  private msgCb: ((m: ServerMessage) => void) | null = null;
  private statusCb: ((s: Status) => void) | null = null;
  private id = 'local';
  private closed = false;

  constructor() {
    const host: RoomHost = {
      now: () => Date.now(),
      send: (_id, msg) => queueMicrotask(() => !this.closed && this.msgCb?.(msg)),
      broadcast: (msg) => queueMicrotask(() => !this.closed && this.msgCb?.(msg)),
      newMeetId: () =>
        Array.from(crypto.getRandomValues(new Uint32Array(4)), (n) => n.toString(16)).join('-'),
      recordMeet: (results, programme) => recordLocalMeet(results, programme),
      getLeague: () => ({ type: 'league', ...loadLocalLeague() }),
    };
    this.room = new Room(host);
  }

  connect(): void {
    this.statusCb?.('open');
  }
  close(): void {
    this.closed = true;
    this.room.leave(this.id);
    this.room.dispose();
    this.statusCb?.('closed');
  }
  send(msg: ClientMessage): void {
    if (this.closed) return;
    this.room.handle(this.id, msg);
  }
  onMessage(cb: (m: ServerMessage) => void): void {
    this.msgCb = cb;
  }
  onStatus(cb: (s: Status) => void): void {
    this.statusCb = cb;
  }
}

// ---------------------------------------------------------------- local league
const LEAGUE_KEY = 'decathlon.league';
interface LocalLeague {
  players: Record<string, LeagueRow>;
  recent: RecentMeet[];
  nextId: number;
}

function loadRaw(): LocalLeague {
  try {
    const raw = localStorage.getItem(LEAGUE_KEY);
    if (raw) return JSON.parse(raw) as LocalLeague;
  } catch {
    /* ignore */
  }
  return { players: {}, recent: [], nextId: 1 };
}

export function loadLocalLeague(): { rows: LeagueRow[]; recent: RecentMeet[] } {
  const l = loadRaw();
  const rows = Object.values(l.players).sort(
    (a, b) => b.points - a.points || b.bestTotal - a.bestTotal,
  );
  return { rows, recent: l.recent };
}

function recordLocalMeet(results: MeetResultRow[], programme: Programme): void {
  const humans = results.filter((r) => !r.isBot);
  if (!humans.length) return;
  const l = loadRaw();
  const now = new Date().toISOString();
  for (const r of humans) {
    if (r.leaguePoints <= 0) continue;
    const key = r.name.toLowerCase();
    const humanPlace = humans.filter((h) => h.place < r.place).length + 1;
    const p = l.players[key] ?? {
      name: r.name,
      meets: 0,
      wins: 0,
      podiums: 0,
      points: 0,
      bestTotal: 0,
      lastPlayed: now,
    };
    p.name = r.name;
    p.meets++;
    p.wins += humanPlace === 1 ? 1 : 0;
    p.podiums += humanPlace <= 3 ? 1 : 0;
    p.points += r.leaguePoints;
    p.bestTotal = Math.max(p.bestTotal, r.total);
    p.lastPlayed = now;
    l.players[key] = p;
  }
  l.recent.unshift({
    id: l.nextId++,
    playedAt: now,
    winner: results.find((r) => r.place === 1)?.name ?? '',
    athletes: humans.length,
    bestTotal: Math.max(...humans.map((h) => h.total)),
    programme: programmeName(programme),
  });
  l.recent = l.recent.slice(0, 10);
  try {
    localStorage.setItem(LEAGUE_KEY, JSON.stringify(l));
  } catch {
    /* ignore */
  }
}
